import React, { useEffect, useState } from 'react'
import { fetchAuthSession, signOut } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { Login } from './Login'
import { useUserStore } from '../../store/userStore'

interface AuthWrapperProps {
  children: React.ReactNode
}

export const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  // Removed signup state as we're only allowing login
  const fetchUserInfo = useUserStore(state => state.fetchUserInfo)
  const { name } = useUserStore(state => state)

  useEffect(() => {
    checkAuthState()

    // Listen for SSO redirect completion
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        checkAuthState()
      }
      if (payload.event === 'signInWithRedirect_failure') {
        console.error('SSO redirect failed:', payload.data)
        setIsLoading(false)
      }
    })

    return () => unsubscribe()
  }, [])

  // Fetch user info when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserInfo()
    }
  }, [isAuthenticated, fetchUserInfo])

  const checkAuthState = async () => {
    console.log('Checking authentication state...');
    try {
      const session = await fetchAuthSession();
      console.log('Auth session result:', {
        hasTokens: !!session.tokens,
        hasIdToken: !!session.tokens?.idToken,
        hasAccessToken: !!session.tokens?.accessToken,
        identityId: session.identityId || 'none'
      });
      
      const isAuth = !!session.tokens;
      console.log('User is authenticated:', isAuth);
      setIsAuthenticated(isAuth);
      
      if (isAuth) {
        // Log token expiration for debugging
        const idToken = session.tokens?.idToken;
        if (idToken) {
          const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
          const expiration = new Date(payload.exp * 1000);
          console.log('ID Token expires at:', expiration.toLocaleString());
        }
      }
    } catch (err) {
      console.error('Error checking auth state:', err);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }

  const handleAuthSuccess = () => {
    setIsAuthenticated(true);
  }

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsAuthenticated(false);
    } catch (err) {
      console.error('Error signing out:', err);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          backgroundImage: 'url("/images/interior-design-background.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 to-indigo-900/40 backdrop-blur-sm"></div>
        <div className="relative z-10 w-full max-w-md">
          <Login onSuccess={handleAuthSuccess} />
        </div>
      </div>
    )
  }

  // isAdmin is already destructured above

  return (
    <div>
      <div className="bg-gradient-to-r from-purple-700 to-indigo-800 text-white p-4 flex justify-between items-center shadow-md">
        <div className="flex items-center">
          <img src="/images/ed-character-logo.png" alt="Ask Ed Logo" className="h-12 mr-3" />
          <h1 className="text-xl font-bold">Ask Ed</h1>
        </div>
        <div className="flex items-center">
          {name && (
            <div className="mr-4 flex items-center">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-medium mr-2">
                {name.charAt(0).toUpperCase()}
              </div>
              <span>Welcome, {name}</span>
            </div>
          )}
          <button 
            onClick={handleSignOut}
            className="px-4 py-2 bg-white text-purple-700 rounded-md hover:bg-white/90 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
      {children}
    </div>
  )
}
